import React, { useState, useEffect, useRef } from 'react';
import { Lock } from 'lucide-react';
import styles from './form.module.css';

const PairMatchingForm = ({ content, onSubmit, isPreview = false, isLocked = false, userAnswer = null, allowUpdateResponse = true }) => {
    const pairs = content?.pairs || [];

    const initMatches = () => {
        if (userAnswer?.matches) {
            const map = {};
            userAnswer.matches.forEach(m => { map[m.left] = m.right; });
            return map;
        }
        return {};
    };

    const [matches, setMatches] = useState(initMatches);
    const [activeLeft, setActiveLeft] = useState(null);

    // Guards against a socket reconnect resync clobbering in-progress, unsubmitted matches: a
    // reconnect re-fetches the activity and hands back a freshly-parsed (but often
    // content-identical) userAnswer object, which would otherwise unconditionally reset
    // `matches` via this effect. Flips true on the participant's first local match/reset since
    // their last actual submit; reset to false right when a submit is fired (local state now IS
    // the submitted state) or when userAnswer goes back to null (fresh activity).
    const hasLocalEditsRef = useRef(false);

    useEffect(() => {
        if (hasLocalEditsRef.current) return;
        setMatches(initMatches());
        setActiveLeft(null);
    }, [userAnswer]);

    const hasSubmitted = userAnswer != null;
    const isDisabled = isLocked || (hasSubmitted && !allowUpdateResponse);
    const allMatched = pairs.every(p => matches[p.left] !== undefined);
    const isUnchanged = hasSubmitted && allMatched && pairs.every(p => {
        const prev = (userAnswer?.matches || []).find(m => m.left === p.left);
        return prev && prev.right === matches[p.left];
    });
    const submitDisabled = isPreview || isLocked || !allMatched || (hasSubmitted && (!allowUpdateResponse || isUnchanged));
    const submitLabel = !hasSubmitted ? (isLocked ? 'Voting Closed' : 'Submit') : (!allowUpdateResponse || isUnchanged ? 'Submitted ✓' : 'Update response');

    const handleLeftClick = (left) => {
        if (isDisabled) return;
        setActiveLeft(prev => prev === left ? null : left);
    };

    const handleRightClick = (right) => {
        if (isDisabled || !activeLeft) return;
        hasLocalEditsRef.current = true;
        setMatches(prev => {
            const next = { ...prev };
            // Remove any existing match for this right item
            Object.keys(next).forEach(k => { if (next[k] === right) delete next[k]; });
            next[activeLeft] = right;
            return next;
        });
        setActiveLeft(null);
    };

    const handleSubmit = () => {
        if (submitDisabled) return;
        const matchArr = pairs.map(p => ({ left: p.left, right: matches[p.left] })).filter(m => m.right !== undefined);
        hasLocalEditsRef.current = false;
        onSubmit({ matches: matchArr });
    };

    const rights = [...pairs].sort((a, b) => a.right > b.right ? 1 : -1).map(p => p.right);

    return (
        <div className={styles.form}>
            <div className={styles.question}>
                {content.question || 'Match the pairs'}
                {isLocked && <span className={styles.lockBadge}><Lock size={12} aria-hidden="true" /> Voting Locked</span>}
            </div>
            <div className={styles.description}>
                {isDisabled ? 'Response recorded.' : activeLeft ? `Matching "${activeLeft}" → select from the right column` : 'Select an item from the left column, then match it to the right.'}
            </div>
            <div className={styles.pairColumns}>
                <div className={styles.pairColumn}>
                    {pairs.map(p => (
                        <button
                            key={p.left}
                            type="button"
                            className={`${styles.pairItem} ${activeLeft === p.left ? styles.active : matches[p.left] ? styles.matched : ''}`}
                            onClick={() => handleLeftClick(p.left)}
                            disabled={isDisabled}
                        >
                            {p.left}
                            {matches[p.left] && <span className={styles.pairItemMatchLabel}>→ {matches[p.left]}</span>}
                        </button>
                    ))}
                </div>
                <div className={styles.pairColumn}>
                    {rights.map(right => {
                        const isAssigned = Object.values(matches).includes(right);
                        return (
                            <button
                                key={right}
                                type="button"
                                className={`${styles.pairItem} ${isAssigned ? styles.matched : activeLeft ? styles.selectable : ''}`}
                                onClick={() => handleRightClick(right)}
                                disabled={isDisabled || !activeLeft}
                            >
                                {right}
                            </button>
                        );
                    })}
                </div>
            </div>
            {!isDisabled && (
                <button
                    type="button"
                    className={styles.ghostBtn}
                    onClick={() => { hasLocalEditsRef.current = true; setMatches({}); setActiveLeft(null); }}
                >
                    Reset
                </button>
            )}
            <button className={styles.submitBtn} onClick={handleSubmit} disabled={submitDisabled}>
                {submitLabel}
            </button>
        </div>
    );
};

export default PairMatchingForm;
